import { DamageType } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import type { PetSystem } from "./petSystem";
import { applyDamage } from "./systems/combat";

/**
 * Bite (Pets v2, docs/PETS.md): the pet fights at the rally flag. Every `intervalTicks` it bites the ground raider
 * nearest the flag's cell within `reach` cells (ties to the lower id; flyers, Burrowers underground and anything else
 * the towers cannot target are out of reach) for `damage` physical damage — the run's copy, already × the towers'
 * level curve at the pet's level. Physical like a troop's blow: armour, Warded and immunity to physical apply
 * (applyDamage); no tower is credited. With nothing in reach it waits, ready, and bites the moment one comes.
 */
export function tickBite(sim: LevelSim, pet: PetSystem): void {
  const bite = pet.rule.bite;
  if (!bite) return;
  if (pet.biteCooldown > 0) { pet.biteCooldown--; return; }
  const target = biteTarget(sim, bite.reach);
  if (target === null) return;
  pet.biteCooldown = bite.intervalTicks;
  sim.events.add(SimEventKind.PetBit, target.id, bite.damage.floorToInt());
  applyDamage(sim, target, null, bite.damage, DamageType.Physical);
}

/**
 * Where a raider would stand for a target choice made then (sim/petForecast.ts projects the raiders ahead): its position
 * and cell, or null to leave it out. The sim's own choices read where they stand now.
 */
export type RaiderAt = (e: Enemy) => { readonly x: Fix64; readonly y: Fix64; readonly cx: number; readonly cy: number } | null;

/** The ground raider nearest the flag's cell within `reach` (ties to the lower id), or null; `at`: where each stands (now). */
export function biteTarget(sim: LevelSim, reach: Fix64, at?: RaiderAt): Enemy | null {
  const r = sim.troops.rally, fx = Fix64.fromInt(r.x), fy = Fix64.fromInt(r.y), r2 = reach.mul(reach), cells = reach.ceilToInt() + 1;
  let best: Enemy | null = null, bestD2 = Fix64.Zero;
  for (const e of sim.enemySlots) {
    if (!e.alive || e.isAir) continue;
    const p = at ? at(e) : e;
    if (p === null || Math.abs(p.cx - r.x) > cells || Math.abs(p.cy - r.y) > cells || !sim.targetable(e)) continue;
    const dx = p.x.sub(fx), dy = p.y.sub(fy), d2 = dx.mul(dx).add(dy.mul(dy));
    if (d2.gt(r2)) continue;
    if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < best.id)) { best = e; bestD2 = d2; }
  }
  return best;
}
